import React, { useContext, useEffect, useState, memo } from 'react';
import { getProtocolRelativePathUrl } from '#/utils/url';
import { ConfigContext } from '#/context/ConfigContext';
import vw from '#/utils/vw';
import style from './imageWithPreload.scss';

const DEFAULT_PLACEHOLDER_IMG_SIZE = 200;

interface ImageCustomStyle {
  height?: string;
  width?: string;
}

interface ImageWithPreloadProps {
  height?: string | number;
  width?: string | number;
  src: string;
  className?: any;
  alt?: string;
  viewAll?: boolean;
  placeholderImgSize?: number;
}

const ImageWithPreload = ({
  src: srcProp,
  className = '',
  alt = '',
  height,
  width,
  viewAll,
  placeholderImgSize
}: ImageWithPreloadProps) => {
  const [src, setSrc] = useState<string | undefined>();
  const [
    containerCustomStyle,
    setContainerCustomStyle
  ] = useState<ImageCustomStyle | null>(null);
  const { config } = useContext(ConfigContext);
  const [imageHasBeenLoaded, setImageHasBeenLoaded] = useState(false);

  const handleSizeValue = (value: string | number | undefined) => {
    if (!value) {
      return '100%';
    }

    if (typeof value === 'number') {
      return `${value}px`;
    }

    return value;
  };

  useEffect(() => {
    if (!srcProp) {
      return;
    }

    // QuickPlay images provides immediately the final url
    if (srcProp.includes('image-resizer')) {
      setSrc(srcProp);
      return;
    }

    const customStyleEdit: ImageCustomStyle = {};

    const heightS = handleSizeValue(height);
    customStyleEdit.height = heightS;

    const widthS = handleSizeValue(width);
    customStyleEdit.width = widthS;

    setContainerCustomStyle(customStyleEdit);
    setSrc(getProtocolRelativePathUrl(srcProp));
  }, [srcProp]);

  const placeholder = {
    backgroundImage: !viewAll ? `url(${config?.appLogo})` : 'url("")',
    backgroundSize: placeholderImgSize
      ? vw(placeholderImgSize)
      : vw(DEFAULT_PLACEHOLDER_IMG_SIZE)
  };

  const animationStyle = imageHasBeenLoaded
    ? style.imgLoaded
    : style.imgLoading;

  return (
    <div
      className={`${style.imgContainer} ${style.imgFallback}`}
      style={{ ...placeholder, ...containerCustomStyle }}
    >
      <div className={`${style.imgContent} ${animationStyle}`}>
        {src && (
          <img
            alt={alt}
            src={src}
            className={className}
            onLoad={() => {
              setImageHasBeenLoaded(true);
            }}
          />
        )}
      </div>
    </div>
  );
};

const propsAreEqual = (
  prevProps: ImageWithPreloadProps,
  nextProps: ImageWithPreloadProps
) => {
  const { src: prevSrc } = prevProps || {};
  const { src: nextSrc } = nextProps || {};

  return prevSrc === nextSrc;
};

export default memo(ImageWithPreload, propsAreEqual);
